import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Mail, Phone, MessageCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, parseISO } from 'date-fns';

interface TimeSlot {
  date: string;
  startTime: string;
  endTime: string;
}

interface AvailabilityData {
  available_slots: any; // Will be cast to TimeSlot[]
  timezone: string;
  preferences?: string;
  contact_preference: string;
  created_at: string;
  updated_at: string;
}

interface InterviewTabProps {
  candidate: {
    full_name: string;
    email: string;
    calendly_url?: string;
  };
  testResponseId: string;
  interviewInvitedAt?: string | null;
}

export const InterviewTab: React.FC<InterviewTabProps> = ({ candidate, testResponseId, interviewInvitedAt }) => {
  const [availability, setAvailability] = useState<AvailabilityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const { data, error } = await supabase
          .from('candidate_interview_availability')
          .select('*')
          .eq('test_response_id', testResponseId)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No availability found
            setAvailability(null);
          } else {
            throw error;
          }
        } else {
          setAvailability({
            ...data,
            available_slots: data.available_slots as unknown as TimeSlot[]
          });
        }
      } catch (err: any) {
        console.error('Error fetching availability:', err);
        setError('Failed to load availability data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [testResponseId]);

  const groupSlotsByDate = (slots: TimeSlot[]) => {
    const grouped: { [date: string]: TimeSlot[] } = {};
    slots.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });
    
    // Sort dates and times
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    
    return grouped;
  };

  const getContactIcon = (preference: string) => {
    switch (preference) {
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'whatsapp': return <MessageCircle className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading availability...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if candidate has been invited to interview
  if (!interviewInvitedAt) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Interview Scheduling
          </CardTitle>
          <CardDescription>
            This candidate hasn't been invited to an interview yet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Not Invited Yet</h3>
            <p className="text-muted-foreground mb-4">
              You haven't invited this candidate to interview yet. Use the "Invite to Interview" button to send them an invitation.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!availability) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Interview Scheduling
          </CardTitle>
          <CardDescription>
            {candidate.full_name} hasn't provided their availability yet
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Availability Set</h3>
            <p className="text-muted-foreground mb-4">
              The candidate hasn't shared their available interview times yet.
            </p>
            {candidate.calendly_url && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">Alternative: Calendly Link</p>
                <a 
                  href={candidate.calendly_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm"
                >
                  {candidate.calendly_url}
                </a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const groupedSlots = groupSlotsByDate(availability.available_slots);
  const sortedDates = Object.keys(groupedSlots).sort();

  return (
    <div className="space-y-6">
      {/* Interview Status Section */}
      {interviewInvitedAt && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Interview Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  Invited on {format(parseISO(interviewInvitedAt), 'MMM d, yyyy')}
                </span>
              </div>
              <Badge variant="outline" className="text-xs">
                {availability ? 'Availability confirmed' : 'Waiting for availability'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Interview Availability
          </CardTitle>
          <CardDescription>
            {candidate.full_name}'s available times for interview scheduling
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Contact Preferences */}
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                {getContactIcon(availability.contact_preference)}
                <span className="text-sm font-medium">
                  Preferred contact: {availability.contact_preference}
                </span>
              </div>
              <Badge variant="outline" className="text-xs">
                Timezone: {availability.timezone}
              </Badge>
            </div>

            {/* Available Time Slots */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Available Time Slots
              </h4>
              
              {sortedDates.length === 0 ? (
                <p className="text-muted-foreground text-sm">No time slots available</p>
              ) : (
                <div className="grid gap-3">
                  {sortedDates.map(date => (
                    <div key={date} className="border rounded-lg p-4">
                      <h5 className="font-medium mb-2">
                        {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {groupedSlots[date].map((slot, index) => (
                          <Badge key={index} variant="secondary">
                            {slot.startTime} - {slot.endTime}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Preferences */}
            {availability.preferences && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Additional Notes</h4>
                <p className="text-sm text-muted-foreground p-3 bg-muted rounded">
                  {availability.preferences}
                </p>
              </div>
            )}

            {/* Last Updated */}
            <div className="text-xs text-muted-foreground pt-2 border-t">
              Last updated: {format(parseISO(availability.updated_at), 'PPpp')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendly Fallback */}
      {candidate.calendly_url && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Alternative Scheduling</CardTitle>
            <CardDescription>
              Direct scheduling link if available times don't work
            </CardDescription>
          </CardHeader>
          <CardContent>
            <a 
              href={candidate.calendly_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <Calendar className="h-4 w-4" />
              Open Calendly Link
            </a>
          </CardContent>
        </Card>
      )}
    </div>
  );
};